import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session timeout: 1 hour of inactivity
const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage cache to prevent flash.
  // CRITICAL: only honour the cached user when we ALSO have a token —
  // otherwise the app renders as "logged in" with a phantom session,
  // every authenticated request 401's, and the 401 interceptor below
  // doesn't recover (it bails when no token is present). The classic
  // visible symptom is the SetUsernameDialog popping up and the
  // availability check failing forever with "could not check".
  const [user, setUser] = useState(() => {
    try {
      const cachedUser = localStorage.getItem('rollm8s_user');
      const cachedToken = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      if (!cachedUser || !cachedToken) {
        // Drop the half-cache so we don't keep limping along on it.
        if (cachedUser && !cachedToken) {
          localStorage.removeItem('rollm8s_user');
          localStorage.removeItem('rollm8s_last_activity');
        }
        return null;
      }
      return JSON.parse(cachedUser);
    } catch {
      return null;
    }
  });
  
  const [loading, setLoading] = useState(() => {
    // If we have a cached user, don't show loading
    const cached = localStorage.getItem('rollm8s_user');
    return !cached;
  });
  
  const [token, setToken] = useState(() => localStorage.getItem('rollm8s_token'));
  const hasCheckedAuth = useRef(false);
  const lastActivityRef = useRef(Date.now());

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    localStorage.setItem('rollm8s_last_activity', String(Date.now()));
  }, []);

  // Check if session has expired due to inactivity
  const isSessionExpired = useCallback(() => {
    const lastActivity = parseInt(localStorage.getItem('rollm8s_last_activity') || '0');
    if (!lastActivity) return false;
    return (Date.now() - lastActivity) > SESSION_TIMEOUT;
  }, []);

  // Clear auth state
  const clearAuth = useCallback(() => {
    localStorage.removeItem('rollm8s_token');
    localStorage.removeItem('token');
    localStorage.removeItem('rollm8s_user');
    localStorage.removeItem('rollm8s_last_activity');
    localStorage.removeItem('rollm8s_music_genre');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  // Configure axios defaults whenever token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }  }, [token]);

  // ── Magic-login from digest email links ────────────────────────────
  // Digest emails carry a single-use `?auth=<token>` param on every
  // link. On the very first mount (and *only* if no active session
  // exists locally) we exchange it for a JWT + user object, then wipe
  // the token from the URL so refresh/back-nav can't accidentally
  // burn a second attempt. Tokens are burned server-side on 1st use.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const magicToken = url.searchParams.get('auth');
    if (!magicToken) return;

    // Strip `auth=` from the URL immediately so react-router / user
    // refresh doesn't re-attempt an already-burned token. Do this even
    // if we bail on the exchange below — the token itself is stale
    // once we've had one crack at it.
    url.searchParams.delete('auth');
    const cleanQs = url.searchParams.toString();
    const cleanUrl = url.pathname + (cleanQs ? `?${cleanQs}` : '') + url.hash;
    window.history.replaceState({}, '', cleanUrl);

    // Don't overwrite an existing session with a magic-token exchange —
    // the user is already logged in, no need to burn the token.
    const existingToken = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
    if (existingToken) return;

    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/magic-login`, { token: magicToken });
        const { token: newToken, user: userData } = data;
        if (!newToken || !userData) return;
        localStorage.setItem('rollm8s_token', newToken);
        localStorage.setItem('token', newToken);
        localStorage.setItem('rollm8s_user', JSON.stringify(userData));
        localStorage.setItem('rollm8s_last_activity', String(Date.now()));
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        setUser(userData);
      } catch {
        // Silent fail — the user just falls through to the guest view.
        // 401 = expired / already-used token, which is the safe default.
      }
    })();
  }, []);

  // Check auth ONLY once on initial mount
  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('rollm8s_token');
    
    if (!storedToken) {
      setLoading(false);
      return;
    }

    // Check for inactivity timeout
    if (isSessionExpired()) {
      console.log('Session expired due to inactivity');
      clearAuth();
      setLoading(false);
      return;
    }

    // If we already have a user cached and token matches, skip API call
    const cachedUser = localStorage.getItem('rollm8s_user');
    if (cachedUser && storedToken === token) {
      setLoading(false);
      updateActivity();
      return;
    }

    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      const response = await axios.get(`${API}/auth/me`, {
        
        timeout: 10000
      });
      
      // Cache user data
      localStorage.setItem('rollm8s_user', JSON.stringify(response.data));
      setUser(response.data);
      setToken(storedToken);
      updateActivity();
      
      // Apply user's theme preferences
      if (response.data.theme_mode) {
        localStorage.setItem('rollm8s_theme_mode', response.data.theme_mode);
      }
      if (response.data.theme_color) {
        localStorage.setItem('rollm8s_theme_color', response.data.theme_color);
      }
    } catch (error) {
      console.log('Auth check failed:', error.response?.status || error.message);
      // Only clear on explicit 401, not on network errors
      if (error.response?.status === 401) {
        clearAuth();
      }
      // On network errors, keep the cached user
    } finally {
      setLoading(false);
    }
  }, [token, isSessionExpired, clearAuth, updateActivity]);

  // Initial auth check - only run once
  useEffect(() => {
    if (!hasCheckedAuth.current) {
      hasCheckedAuth.current = true;
      checkAuth();
    }
  }, [checkAuth]);

  // Global axios 401 interceptor — auto-recover from stale/invalid JWTs
  // (e.g. after a production redeploy that regenerated JWT_SECRET). This
  // protects every authenticated API call in the app, not just /auth/me.
  //
  // Paired with a REQUEST interceptor that pre-emptively rewrites any
  // outbound `Bearer null` / `Bearer undefined` header (caused by stale
  // React closures sending `Bearer ${token}` before state hydrates).
  // Without this, ~310 callsites scattered across the app could each
  // independently boot the user to /login on a 401. Belt-and-braces:
  // the request interceptor stops it happening, the response one
  // recovers cleanly if it ever does.
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const headers = config.headers || {};
      const auth = headers.Authorization || headers.authorization;
      if (typeof auth === 'string' && /^Bearer\s+(null|undefined)?\s*$/i.test(auth)) {
        const live = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        if (live) {
          headers.Authorization = `Bearer ${live}`;
        } else {
          // No usable token anywhere — drop the malformed header so the
          // backend returns a clean 401 (not 422 on bad creds parsing).
          delete headers.Authorization;
          delete headers.authorization;
        }
        config.headers = headers;
      }
      return config;
    });

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || '';
        // Skip login/register calls — those legitimately return 401 on bad creds.
        const isAuthEndpoint = url.includes('/api/auth/login') ||
                               url.includes('/api/auth/register') ||
                               url.includes('/api/auth/verify');
        if (status === 401 && !isAuthEndpoint) {
          // Recover from a stale session. We used to bail when the
          // localStorage token was already absent — but that's exactly
          // the state that produces phantom sessions (cached `user` with
          // a missing `rollm8s_token`), so the user got stranded with no
          // redirect. Now: if anything in localStorage still looks like
          // a logged-in session (token OR cached user), wipe and bounce
          // to /login.
          const hasToken = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
          const hasCachedUser = localStorage.getItem('rollm8s_user');
          if (hasToken || hasCachedUser) {
            try { toast.error('Session expired — please log in again.'); } catch { /* toast unavailable — ignore */ }
            clearAuth();
            const next = window.location.pathname + window.location.search;
            if (!window.location.pathname.startsWith('/login')) {
              window.location.replace(`/login?next=${encodeURIComponent(next)}`);
            }
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(interceptor);
    };
  }, [clearAuth]);

  // Activity tracking for session timeout
  useEffect(() => {
    if (!token) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      updateActivity();
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Periodic check for session expiry
    const interval = setInterval(() => {
      if (isSessionExpired()) {
        console.log('Session timeout - logging out');
        clearAuth();
      }
    }, 60000); // Check every minute

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearInterval(interval);
    };
  }, [token, updateActivity, isSessionExpired, clearAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    // Store token under both keys for compatibility
    localStorage.setItem('rollm8s_token', newToken);
    localStorage.setItem('token', newToken);
    localStorage.setItem('rollm8s_user', JSON.stringify(userData));
    localStorage.setItem('rollm8s_last_activity', String(Date.now()));
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    
    localStorage.setItem('rollm8s_token', newToken);
    localStorage.setItem('token', newToken);
    localStorage.setItem('rollm8s_user', JSON.stringify(newUser));
    localStorage.setItem('rollm8s_last_activity', String(Date.now()));
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Ignore logout errors
    }
    clearAuth();
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API}/auth/profile`, data);
    localStorage.setItem('rollm8s_user', JSON.stringify(response.data));
    setUser(response.data);
    return response.data;
  };

  // Handle Google OAuth session
  const handleOAuthSession = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, {}, {
      headers: { 'X-Session-ID': sessionId },
      
    });
    const { session_token, ...userData } = response.data;
    
    // Store token under both keys for compatibility
    localStorage.setItem('rollm8s_token', session_token);
    localStorage.setItem('token', session_token);
    localStorage.setItem('rollm8s_user', JSON.stringify(userData));
    localStorage.setItem('rollm8s_last_activity', String(Date.now()));
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${session_token}`;
    setToken(session_token);
    setUser(userData);
    return userData;
  };

  // Optimistically merge a partial patch into the current user state AND
  // localStorage cache. Used by flows like SetUsernameDialog where the
  // POST endpoint returns just `{success, username}` and we don't want
  // to rely on a follow-up GET (refreshUser) that could silently fail
  // and trap the user on a stale `username_auto_generated: true` cache.
  const updateUser = useCallback((patch) => {
    if (!patch || typeof patch !== 'object') return;
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      try {
        localStorage.setItem('rollm8s_user', JSON.stringify(next));
      } catch { /* localStorage quota / private mode — safe to skip */ }
      return next;
    });
  }, []);

  // Force refresh user data from server
  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem('rollm8s_token');
    if (!storedToken) return;
    
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      const response = await axios.get(`${API}/auth/me`, {
        
        timeout: 10000
      });
      
      // Update cached user data
      localStorage.setItem('rollm8s_user', JSON.stringify(response.data));
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.log('Refresh user failed:', error.response?.status || error.message);
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      updateProfile,
      handleOAuthSession,
      checkAuth,
      refreshUser,
      updateUser,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
